// eslint-disable-next-line import/named
import { cache } from '../../cache-impl/cache-registry';
import { customElement } from 'lit/decorators.js';
import { DataBinding } from '../../../../webmodule-common/other/ui/databinding/databinding.js';
import { DataTracker, FieldType } from '../../../../webmodule-common/other/ui/databinding/data-tracker.js';
import { getApiFactory } from '../../../api/api-injector.js';
import { html } from 'lit';
import { isEmptyOrSpace } from '../../../../webmodule-common/other/ui/string-helper-functions.js';
import { LangFranchiseeQuoteItemsView } from '../../../../localization/scripts/dealer-franchisee/quotes/views/franchisee-quote-items-view-lang';
import { LangFranchiseeQuoteListView } from '../../../../localization/scripts/dealer-franchisee/quotes/views/franchisee-quote-list-view-lang';
import { TermsConditionsPicker } from '../../../franchisee/view/terms-conditions-picker';
import { userDataStore } from '../../common/current-user-data-store.js';

import type { DataCache } from '../../../../webmodule-common/cache/data-cache';
import type { EventTemplate, Snippet } from '../../../../webmodule-common/other/ui/events.js';
import type { FranchiseeQuoteContainerManager } from '../data/franchisee-quote-manager';
import type { QuoteContainerManager } from '../../../quotes/data/quote-container';
import type { SettingsManager } from '../../../quotes/views/quote-items-view.js';
import type { TermsAndConditionsCacheData } from '../../cache-impl/cache-data';
import type { WebmoduleSelectEvent } from '../../../../webmodule-common/components/src/events/webmodule-select';
import { ViewBase } from '../../../../webmodule-common/other/ui/view-base';

export interface FranchiseeQuoteTermsViewOptions {
  quoteManager: QuoteContainerManager;
}

@customElement('wm-franchiseequotetermsview')
export class FranchiseeQuoteTermsView extends ViewBase {
  protected readonly quoteManager: QuoteContainerManager;
  protected settingsManager?: SettingsManager;

  dataBinding: DataBinding;
  dataTracker: DataTracker;
  api = getApiFactory().termsAndConditions();
  termsConditionsCache: DataCache<TermsAndConditionsCacheData> = cache().termsAndConditions;

  constructor(options: FranchiseeQuoteTermsViewOptions) {
    super();

    this.quoteManager = options.quoteManager;

    this.dataBinding = new DataBinding(
      this.ui,
      undefined,
      (input: string, internalId: string) => `quoteterms-${input}-${internalId}`
    );
    this.dataTracker = new DataTracker(this.dataBinding);

    const addField = (
      fieldName: string,
      propertyType?: FieldType,
      nullable?: boolean,
      editorFieldName?: string,
      data?: () => any
    ) => {
      this.dataTracker.addObjectBinding(
        data ?? (() => this.quoteManager.quote),
        fieldName,
        editorFieldName ?? fieldName,
        propertyType ?? FieldType.string,
        nullable ?? false
      );
    };

    addField('termsAndConditions', FieldType.string, true);
  }

  protected get qcm(): FranchiseeQuoteContainerManager {
    return this.quoteManager as FranchiseeQuoteContainerManager;
  }

  buttonMenu(): Snippet {
    if (this.quoteManager.isReadonly()) return html``;

    return html`
      <webmodule-dropdown
        placement="top-start"
        distance="5"
        @webmodule-select=${(e: WebmoduleSelectEvent) => this.onTermsMenuClick(e)}
      >
        <webmodule-button size="small" slot="trigger" variant="default" caret caretup>
          ${LangFranchiseeQuoteListView.termsButton()}
        </webmodule-button>
        <webmodule-menu>
          <webmodule-menu-item value="pick">${LangFranchiseeQuoteListView.termsPickButton()}</webmodule-menu-item>
          <webmodule-divider></webmodule-divider>
          <webmodule-menu-item value="clear">${LangFranchiseeQuoteListView.termsClearButton()}</webmodule-menu-item>
        </webmodule-menu>
      </webmodule-dropdown>
    `;
  }

  public async refreshData(): Promise<void> {
    this.requestUpdate();
  }

  async onTermsMenuClick(e: WebmoduleSelectEvent) {
    const item = e.detail.item;

    switch (item.value) {
      case 'pick':
        {
          const selectedId = await TermsConditionsPicker(this.api);
          if (selectedId) {
            const toc = await this.termsConditionsCache.get(selectedId.id);
            if (toc) {
              this.quoteManager.quote.termsAndConditions = toc.data.termsAndConditions.terms;
              this.requestUpdate();
            }
          }
        }
        break;
      case 'clear':
        this.quoteManager.quote.termsAndConditions = null;
        this.requestUpdate();
        break;
      default:
        break;
    }
  }

  async afterConstruction(): Promise<void> {
    await super.afterConstruction();
  }

  public async prepareForSave() {
    if (this.quoteManager.isReadonly()) return;

    this.dataTracker.applyChangeToValue();
  }

  protected template(): EventTemplate {
    const dealerName = userDataStore.franchisee.name;
    const termsAndConditions = isEmptyOrSpace(dealerName)
      ? LangFranchiseeQuoteItemsView.dealerTermsAndConditions()
      : LangFranchiseeQuoteItemsView.dealerNameTermsAndConditions({ dealerName });

    return html`
      <form class="py-3 px-0 form-one-col ">
        <div class="row">
          <div class="quote-items-terms">
            <div class="row mb-2 align-items-center form-col-item">
              <webmodule-textarea
                class="label-on-top quote-tnc-block"
                id=${this.dataBinding.field('termsAndConditions')}
                value=${this.dataTracker.getObjectDisplayValue('termsAndConditions') || ''}
                label=${termsAndConditions}
                ?readonly=${this.quoteManager.isReadonly()}
                maxlength="10000"
                rows="15"
              ></webmodule-textarea>
            </div>
          </div>
        </div>
      </form>
    `;
  }
}
